import { Link } from 'react-router';
import { useGarage } from '../../garage/queries';
import { describeVehicle } from '../../garage/format';
import { Badge, EmptyState, ErrorState, Heading, Spinner, Stack, Text, Thumbnail } from '../../ui';

/**
 * The garage: every vehicle the signed-in user owns.
 *
 * @returns The garage list screen.
 */
export function GarageListPage(): React.JSX.Element {
  const { data, isPending, isError, error, refetch } = useGarage();

  return (
    <Stack gap="xl">
      <Stack direction="row" gap="md" align="center" justify="space-between" wrap>
        <Heading level={1}>Garage</Heading>
        <Stack direction="row" gap="sm" wrap>
          <a className="gm-button gm-button--secondary" href="/api/garage/export">
            Export
          </a>
          <Link className="gm-button gm-button--primary" to="/garage/new">
            Add a vehicle
          </Link>
        </Stack>
      </Stack>

      {isPending && (
        <Stack gap="md" align="center">
          <Spinner label="Loading your garage" />
          <Text muted>Loading your garage…</Text>
        </Stack>
      )}

      {isError && <ErrorState message={error.message} onRetry={() => void refetch()} />}

      {!isPending && !isError && data.length === 0 && (
        <EmptyState
          title="No vehicles yet"
          description="Add your first car and start tracking what you have done to it."
          action={
            <Link className="gm-button gm-button--primary" to="/garage/new">
              Add a vehicle
            </Link>
          }
        />
      )}

      {!isPending && !isError && data.length > 0 && (
        <div className="gm-grid">
          {data.map((vehicle) => (
            <Link key={vehicle.id} to={`/garage/${vehicle.id}`} className="gm-vehicle-card">
              <Thumbnail src={vehicle.thumbnailUrl ?? undefined} alt={describeVehicle(vehicle)} size={220} />
              <Stack gap="xs">
                <Heading level={2} size="lg">
                  {describeVehicle(vehicle)}
                </Heading>
                <Text size="sm" muted>
                  {vehicle.year} {vehicle.make} {vehicle.model}
                  {vehicle.trim === null || vehicle.trim === undefined ? '' : ` ${vehicle.trim}`}
                </Text>
              </Stack>
              <Stack direction="row" gap="sm" wrap align="center">
                <Badge tone="neutral">
                  {vehicle.modificationCount} {vehicle.modificationCount === 1 ? 'mod' : 'mods'}
                </Badge>
                {vehicle.isPublic && <Badge tone="accent">Public</Badge>}
              </Stack>
            </Link>
          ))}
        </div>
      )}
    </Stack>
  );
}
